/**
 * Medya teklifi formu için yardımcı sabitler ve API → form dönüşüm fonksiyonu.
 * @module AddMediaContent/helpers
 */
import { CustomerServiceOutlined, FileImageOutlined, VideoCameraOutlined } from '@ant-design/icons';

/**
 * Medya türü → ikon bileşeni eşlemesi (tek kaynak). Hem tablo thumbnail fallback'i
 * (`ArticleOrderMedia`) hem yüklenen dosya grid kartları (`UploadArea`) buradan türer.
 * Fotoğraf ve bilinmeyen türler dosya-görsel ikonuna düşer; video/ses kendi ikonları.
 * @type {Record<string, React.ComponentType>}
 */
const MEDIA_TYPE_ICON = {
  photo: FileImageOutlined,
  video: VideoCameraOutlined,
  audio: CustomerServiceOutlined,
};

/**
 * Bir medya türü için ikon bileşenini döndürür (bilinmeyen tür → dosya-görsel ikonu).
 * @param {string} mediaType - 'photo' | 'video' | 'audio'
 * @returns {React.ComponentType} antd ikon bileşeni
 */
export const getMediaTypeIcon = (mediaType) => MEDIA_TYPE_ICON[mediaType] || FileImageOutlined;

/**
 * Bir medya/dosya kaydından küçük-resim (poster/thumbnail) URL'ini tek kuralla çözer.
 * Hem tablo "Medya" hücresi (`ArticleOrderMedia`) hem yüklenen dosya grid kartları (`UploadArea`)
 * buradan türer; aksi halde aynı veri iki yerde farklı gösterilir.
 *
 * Öncelik: yükleme anı `file.poster` → media `poster[0].url` → `thumbnailFullUrl`. Bunların
 * hiçbiri yoksa ve tür fotoğrafsa dosyanın kendi URL'ine (`fileFullUrl`/`url`) düşülür;
 * video/ses için poster yoksa `null` döner (çağıran tür ikonuna düşer).
 *
 * @param {Object} file - Upload anı ({ response: media }) veya media/edit shape ({...media})
 * @param {string} mediaType - 'photo' | 'video' | 'audio'
 * @returns {string|null} Küçük resim URL'i ya da null
 */
export const resolvePosterUrl = (file, mediaType) => {
  if (!file) return null;
  const m = file.response || file;
  const poster = file?.poster?.[0]?.url || m?.poster?.[0]?.url || m?.thumbnailFullUrl || null;
  if (poster) return poster;
  if (mediaType === 'photo') return m?.fileFullUrl || file?.url || null;
  return null;
};

/**
 * antd Image lightbox'ında güvenle önizlenebilen raster/vektör görsel uzantıları.
 * `imageFileType` PDF/DOCX'i de "photo" olarak kabul eder ama bunlar önizleme penceresinde
 * kırık görünür → büyük görüntüleme aksiyonundan hariç tutulur.
 */
const PREVIEWABLE_IMAGE_EXT = /\.(jpe?g|png|bmp|tiff?|webp|svg)$/i;

/**
 * Bir medyanın tam-boy görsel olarak (lightbox'ta) önizlenebilir olup olmadığı — tek kaynak.
 * Hem "Dosya Yönetimi" modalı (`UploadedMediaModal`) hem işlem geçmişi medya chip'leri
 * buradan türer; aksi halde iki yerde iki farklı uzantı listesi oluşurdu.
 *
 * @param {{ name?: string, fileFullUrl?: string }} [media] - Medya nesnesi
 * @param {string} [mediaType] - Grup düzeyindeki tür ('photo' | 'video' | 'audio' | ...)
 * @returns {boolean} Yalnız `photo` türünde, dosya URL'i olan ve gerçek görsel uzantılı ise true
 */
export const isPreviewableMedia = (media, mediaType) =>
  mediaType === 'photo' && Boolean(media?.fileFullUrl) && PREVIEWABLE_IMAGE_EXT.test(media?.name || media?.fileFullUrl || '');

/**
 * Medya türü → görünen etiket eşlemesi (tek kaynak).
 * BE kontratı `MediaOfferMediaType` enum'una göre string anahtarlar.
 * Hem tablo lookup'ı (`MEDIA_TYPE_LABELS[value]`) hem Select listesi (`mediaTypeOptions`) buradan türer.
 * @type {Record<string, string>}
 */
export const MEDIA_TYPE_LABELS = {
  photo: 'Fotoğraf',
  video: 'Video',
  audio: 'Ses Dosyası',
  map: 'Harita',
  chart: 'Grafik',
  infographic: 'İnfografik',
  plan: 'Plan',
  sheet_music: 'Nota',
  flag: 'Bayrak',
  picture: 'Resim',
  miniature: 'Minyatür',
  calligraphy: 'Hat',
  manuscript_page: 'Yazma Sayfası',
};

/**
 * Medya türü seçenekleri (Select için label/value listesi); `MEDIA_TYPE_LABELS`'ten türetilir.
 * @type {Array<{ label: string, value: string }>}
 */
export const mediaTypeOptions = Object.entries(MEDIA_TYPE_LABELS).map(([value, label]) => ({ label, value }));

/**
 * Medya teklifi modalının kendi form alanları.
 *
 * Modal, parent (madde gönderme) formuyla AYNI form instance'ını paylaşır; bu yüzden
 * argümansız `form.resetFields()` çağrısı Madde Dosyası (`itemFile`), Kimlik Kartı (`idCard`)
 * gibi parent alanları da sıfırlar. Modal kapanışında yalnızca bu listedeki alanlar
 * resetlenmelidir (antd `resetFields(nameList)` native API'si).
 * @type {string[]}
 */
export const MEDIA_OFFER_FORM_FIELDS = [
  'media',
  'mediaType',
  'copyright',
  'description',
  'subtitle',
  'archiveLink',
  'source',
  'link',
  'copyrightStatus',
];

/**
 * BE alan adı → Türkçe etiket eşlemesi (tek kaynak). İşlem geçmişi (`field_change`
 * satırları) buradan okur; form/tablo etiketleriyle tutarlılık için yeni kullanım
 * noktaları da bu haritadan beslenmelidir.
 * Anahtarlar BE entity property adlarıdır (history API `field` alanıyla birebir).
 * @type {Record<string, string>}
 */
export const MEDIA_OFFER_FIELD_LABELS = {
  copyright: 'Telif Türü',
  copyrightStatus: 'Telif Durumu',
  source: 'Kaynak',
  description: 'Açıklama',
  subtitle: 'Alt Yazı',
  link: 'Link',
  archiveLink: 'Arşiv Linki',
};

/**
 * Enum taşıyan alanların ham değer → kısa Türkçe etiket eşlemesi.
 * Form radio'larındaki uzun onay cümleleri ("Verdiğim medyanın telifi bana aittir ve…")
 * geçmiş/diff satırına sığmadığı için buradaki kısa karşılıklar kullanılır.
 * Haritada olmayan alanların değerleri ham (scalar) gösterilir.
 * @type {Record<string, Record<string, string>>}
 */
export const MEDIA_OFFER_FIELD_VALUE_LABELS = {
  copyright: {
    suggestion: 'Medya Önerisi',
    generate: 'Medya Üretimi',
  },
  copyrightStatus: {
    claimed: 'Telif Bende',
    un_claimed: 'Telif Bana Ait Değil',
  },
};

/**
 * Bir alanın ham değerini görünen metne çevirir: enum alanlarda kısa etiket,
 * diğerlerinde değerin kendisi. `null`/boş değerler için `null` döner
 * (çağıran taraf "boş" gösterimine kendisi karar verir).
 * @param {string} field - BE alan adı (`MEDIA_OFFER_FIELD_LABELS` anahtarı)
 * @param {*} value - Ham değer (scalar/string ya da null)
 * @returns {string|null} Görünen metin ya da null
 */
export const formatMediaOfferFieldValue = (field, value) => {
  if (value === null || value === undefined || value === '') return null;
  return MEDIA_OFFER_FIELD_VALUE_LABELS[field]?.[value] ?? String(value);
};

/**
 * Kaydetmeden önce doğrulanacak medya teklifi form alanları (4 modalda ortak) —
 * yazar Ekle/Düzenle + heyet/TS Ekle/Düzenle tek listeden beslenir.
 *
 * Liste, kuralı olmayan alanları (`description`, `media`) da içerir: bunlar artık zorunlu
 * olmadığı için doğrulama no-op'tur, ancak kural sonradan eklenirse listeyi güncellemek
 * gerekmesin diye alan seti tam bırakılmıştır.
 * @type {string[]}
 */
export const MEDIA_OFFER_VALIDATE_FIELDS = ['mediaType', 'copyright', 'description', 'source', 'link', 'copyrightStatus', 'media'];

/**
 * Medya ekleme/düzenleme modallarının ortak görünüm propları (genişlik, scroll, buton metinleri).
 * Başlık (title) her modale özeldir; ayrıca verilir.
 * @type {Object}
 */
export const MEDIA_MODAL_PROPS = {
  width: '85%',
  bodyStyle: { maxHeight: '70vh', overflowY: 'auto' },
  okText: 'Kaydet',
  cancelText: 'İptal',
  destroyOnClose: true,
};

/** Upload için kabul edilen görsel/doküman uzantıları (virgülle ayrılmış). */
export const imageFileType = '.jpg, .jpeg, .png, .gif, .webp, .tiff, .bmp, .pdf, .docx, .svg';
/** Upload için kabul edilen video uzantıları (virgülle ayrılmış). */
export const videoFileType = '.mp4, .avi, .mkv, .mov, .flv, .wmv, .webm';
/** Upload için kabul edilen ses uzantıları (virgülle ayrılmış). */
export const soundFileType = '.mp3, .wav, .flac, .aac, .ogg, .wma';

const MB = 1024 * 1024;
/** Bariz türler (photo/video/audio) için üst dosya boyutu sınırı (byte). */
export const MAX_SIZE_STRICT = 25 * MB;
/** Belirsiz/kaynak türler (harita, minyatür, hat…) için üst dosya boyutu sınırı (byte). PSD/ZIP büyük olabildiğinden daha yüksek. */
export const MAX_SIZE_SOURCE = 100 * MB;

/**
 * Uzantısı net olan medya türleri → izinli uzantı listesi (whitelist mantığı).
 * Buradaki türler yalnız kendi listelerindeki uzantıları kabul eder; listede olmayanların
 * hepsi (belirsiz türler) deny-list mantığına düşer.
 * @type {Record<string, string>}
 */
const WHITELIST_BY_TYPE = {
  photo: imageFileType,
  video: videoFileType,
  audio: soundFileType,
};

/**
 * Güvenlik nedeniyle her zaman reddedilen uzantılar — belirsiz türlerin deny-list'i.
 * Çalıştırılabilir/kurulum, script, tarayıcıda çalışan/XSS ve makro içeren Office dosyaları.
 * NOT: Doğrulama tamamen client-side'dır; uzantı değiştirme veya doğrudan API çağrısı ile
 * aşılabilir. Bu liste "kaza + amatör kötüye kullanım" caydırıcısıdır, sunucu tarafı güvenlik değildir.
 * @type {string[]}
 */
export const DENIED_EXTENSIONS = [
  // Çalıştırılabilir / kurulum
  '.exe', '.msi', '.dll', '.com', '.scr', '.pif', '.cpl', '.bat', '.cmd',
  '.app', '.dmg', '.pkg', '.deb', '.rpm', '.apk', '.jar', '.msc', '.gadget',
  // Script
  '.sh', '.bash', '.zsh', '.ps1', '.psm1', '.vbs', '.vbe', '.js', '.mjs',
  '.jse', '.wsf', '.wsh', '.hta', '.reg', '.lnk',
  // Tarayıcıda çalışan / XSS
  '.html', '.htm', '.xhtml', '.shtml', '.mhtml', '.svg', '.swf',
  // Makro içeren Office
  '.docm', '.dotm', '.xlsm', '.xltm', '.pptm', '.potm', '.ppsm',
];

/** Virgülle ayrılmış uzantı string'ini ('.a, .b') küçük harfli diziye çevirir. */
const toExtList = (str) => str.split(',').map((ext) => ext.trim().toLowerCase());

/**
 * Seçili medya türü → `accept` prop'u için uzantı listesi (dosya seçici filtresi).
 * Bariz türler kendi whitelist'ini döndürür; belirsiz türlerde `''` döner (seçici tüm türleri
 * gösterir, asıl kısıt deny-list ile `beforeUpload`'ta uygulanır).
 * @param {string} mediaType - `MEDIA_TYPE_LABELS` anahtarlarından biri
 * @returns {string} `accept` değeri (whitelist string ya da boş string)
 */
export const getAcceptedFileType = (mediaType) => WHITELIST_BY_TYPE[mediaType] || '';

/**
 * Bir dosyanın seçili medya türü için yüklenebilir olup olmadığını uzantısına göre belirler.
 * Bariz türlerde whitelist eşleşmesi aranır; belirsiz türlerde deny-list dışı olması yeterlidir.
 * @param {string} fileName - Dosya adı (uzantı dahil)
 * @param {string} mediaType - `MEDIA_TYPE_LABELS` anahtarlarından biri
 * @returns {boolean} Yüklemeye izin veriliyorsa true
 */
export const isExtensionAllowed = (fileName, mediaType) => {
  const name = (fileName || '').toLowerCase();
  const whitelist = WHITELIST_BY_TYPE[mediaType];
  if (whitelist) return toExtList(whitelist).some((ext) => name.endsWith(ext));
  return !DENIED_EXTENSIONS.some((ext) => name.endsWith(ext));
};

/**
 * Seçili medya türü için izin verilen en büyük dosya boyutu (byte).
 * Bariz türler `MAX_SIZE_STRICT`, belirsiz/kaynak türler `MAX_SIZE_SOURCE`.
 * @param {string} mediaType - `MEDIA_TYPE_LABELS` anahtarlarından biri
 * @returns {number} Byte cinsinden üst sınır
 */
export const getMaxFileSize = (mediaType) => (WHITELIST_BY_TYPE[mediaType] ? MAX_SIZE_STRICT : MAX_SIZE_SOURCE);

/**
 * Bir medya teklifi grubunu (`MediaOfferGroup`) düzenleme formunun alanlarına eşler.
 * Supervisor ve yazar edit modalleri aynı doldurma mantığını paylaşır.
 *
 * `mediaType` GRUP düzeyinde sabittir (versiyonlar arasında değişmez) ve yalnız grubun
 * kendisinden okunur — kontrat gereği tek doğru kaynak budur (bkz. veri modeli:
 * `MediaOfferGroup` → `currentVersion`). Geri kalan alanlar güncel versiyondan gelir.
 *
 * @param {Object} group - `getMediaOfferGroupById` ile gelen grup; `mediaType` + `currentVersion` taşır
 * @returns {Object} `form.setFieldsValue` ile kullanılan alan eşlemesi
 */
export const mapVersionToForm = (group) => {
  const version = group?.currentVersion ?? {};
  return {
    // mediaType grup düzeyinde sabittir; sözleşmenin tek doğru kaynağı grup nesnesidir
    mediaType: group?.mediaType,
    copyright: version.copyright,
    copyrightStatus: version.copyrightStatus,
    source: version.source,
    description: version.description,
    subtitle: version.subtitle,
    archiveLink: version.archiveLink,
    link: version.link,
    media: (version.mediaOfferItems || []).map((item) => ({
      ...item.media,
      mediaID: item.media['@id'],
    })),
  };
};
